import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { Loader2 } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/cn';

/**
 * Button — spec'd in handoff/COMPONENT_SPECS.md.
 *
 * 7 variants: primary, secondary, outline, ghost, destructive, link, orange.
 * Sizes: sm 36 / default 40 / lg 44 / icon 40.
 * Supports `loading` (renders spinner + disables) and `asChild` (Radix Slot).
 */

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded text-sm font-medium ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 transition-colors',
  {
    variants: {
      variant: {
        // Navy-on-orange (5.09:1, passes WCAG AA) — matches the marketing button.
        //
        // `text-primary-foreground`, NOT `text-navy-primary`. The comment that
        // stood here said the reverse — "the old text-primary-foreground was
        // white-on-orange (2.26:1, FAILED AA)" — and that WAS true until
        // 2026-08-19, when the token was corrected to the navy for this exact
        // reason. The local override outlived the bug it worked around.
        //
        // It became a live defect the moment a dark theme existed. `navy-primary`
        // has to resolve to a LIGHT navy (#8FB3D9) in dark, because elsewhere it
        // is heading TEXT — so this button rendered pale blue on orange at
        // roughly 1.7:1. Caught by looking at the running app; every static gate
        // passed, because none of them knew this pairing existed.
        //
        // `primary-foreground` is #1e3a5f in BOTH themes and is defined for one
        // job: whatever sits on the primary. Use it, and this cannot drift again.
        primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/90',
        outline:
          'border border-border bg-card text-foreground hover:bg-muted',
        ghost: 'text-foreground hover:bg-muted',
        // -dark fills, not the DEFAULT ones. White on #e53e3e is 4.11:1 and on
        // #dd6b20 is 3.39:1; both fail AA for 14px semibold, and `orange`
        // badly. The -dark shades fix that in the LIGHT theme.
        //
        // The label is `text-background`, NOT `text-white`, and that matters:
        // the `-dark` tokens are TEXT shades, so they resolve to LIGHT colours
        // in dark theme (#FF8078, #E8A93C) where white on them measures
        // 2.44:1 and 2.06:1 — worse than the failure the -dark shades were
        // introduced to fix. `background` is the inverse-of-the-surface token:
        // 5.00/7.58 and 4.83/8.95, AA in both themes.
        //
        // Third instance of this exact shape today, after Button.primary and
        // Avatar. The pattern to watch for: a solid fill whose token flips
        // lightness between themes, with a hardcoded foreground that cannot.
        destructive: 'bg-danger-dark text-background hover:bg-danger-dark/90',
        // `text-primary` is 2.04:1 on a light ground — see --color-primary-ink.
        link: 'text-primary-ink underline-offset-4 hover:underline',
        orange: 'bg-warning-dark text-background hover:bg-warning-dark/90',
        // Marketing voice — uppercase + tracked + sharp + lift + primary glow.
        // Architect's A11y lock (2026-05-08): bg-primary + text-navy-primary
        // is 5.09:1 — PASSES WCAG AA. bg-primary + text-white was 2.26:1 —
        // FAILED AA. The marketing site's white-on-orange, noted here as a
        // follow-up, was closed 2026-08-11 along with the underlying
        // --color-primary-foreground token. No brackets on filled variant
        // (would be invisible on the orange); ghost + subtle keep brackets.
        // Spec: PORTAL_VISUAL_ALIGNMENT_IMPLEMENTATION_PLAN_2026-05-08.md §VA2.2
        marketing:
          'bg-primary text-primary-foreground uppercase tracking-[0.15em] font-semibold rounded-sm hover:bg-primary/90 hover:-translate-y-px hover:shadow-glow-primary transition-all duration-200',
        'marketing-ghost':
          'brackets-hover border border-border bg-transparent text-foreground uppercase tracking-[0.15em] font-semibold rounded-sm hover:border-primary/50 hover:bg-primary/5 hover:-translate-y-px hover:shadow-glow-primary-sm transition-all duration-200',
        'marketing-subtle':
          'brackets-hover bg-muted border border-border text-foreground uppercase tracking-[0.15em] font-semibold rounded-sm hover:bg-muted/80 hover:border-primary/40 hover:-translate-y-px transition-all duration-200',
      },
      size: {
        sm: 'h-9 px-3',
        default: 'h-10 px-4 py-2',
        lg: 'h-11 px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, loading = false, disabled, children, ...props },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? (
          <>
            <Loader2 className="animate-spin" aria-hidden />
            {children}
          </>
        ) : (
          children
        )}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
